/**
 * Protocol v2 sync API client.
 *
 * All timestamps on the wire are epoch-millisecond integers (see
 * docs/sync-protocol.md §3) — the class of bugs where ISO-8601 seconds
 * truncated away sub-second precision does not exist here.
 */
import { api } from './client.js';
import { deviceId } from '../sync/deviceId.js';

export type WireChangeKind = 'note' | 'folder' | 'attachment' | 'vault_meta' | 'vault_dek';
export type WireChangeOp = 'upsert' | 'delete';

export interface WireNote {
  id: string;
  folderId: string | null;
  title: string;
  content: string;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
  trashedFrom: string | null;
  isVaulted: boolean;
  vaultCiphertextBase64: string | null;
  vaultedFrom: string | null;
  version: number;
  revision: number;
}

export interface WireFolder {
  id: string;
  name: string;
  parentId: string | null;
  sortOrder: number | null;
  createdAt: number;
  updatedAt: number;
  version: number;
  revision: number;
}

export interface WireAttachment {
  id: string;
  filename: string;
  hash: string;
  size: number;
  updatedAt: number;
}

export interface WireVaultMeta {
  saltBase64: string;
  verifierBase64: string;
  updatedAt: number;
}

export interface WireVaultDek {
  wrappedBase64: string;
  updatedAt: number;
}

export type WireRecord =
  | WireNote
  | WireFolder
  | WireAttachment
  | WireVaultMeta
  | WireVaultDek;

export interface WireChange {
  seq: number;
  kind: WireChangeKind;
  op: WireChangeOp;
  objectId: string;
  record: unknown;
}

/** A local change published through POST /sync/push. */
export type LocalChange =
  | { kind: 'note'; op: 'upsert'; record: WireNote }
  | { kind: 'note'; op: 'delete'; id: string; deletedAt: number; baseVersion?: number }
  | { kind: 'folder'; op: 'upsert'; record: WireFolder }
  | { kind: 'folder'; op: 'delete'; id: string; deletedAt: number; baseVersion?: number }
  | { kind: 'attachment'; op: 'upsert'; record: WireAttachment }
  | { kind: 'vault_meta'; op: 'upsert'; record: WireVaultMeta }
  | { kind: 'vault_dek'; op: 'upsert'; record: WireVaultDek };

export interface PushAccepted {
  kind: WireChangeKind;
  id: string;
  version: number;
  revision: number;
}

export interface PushConflict {
  kind: WireChangeKind;
  id: string;
  reason: 'stale_version';
  theirs: WireNote | WireFolder | null;
}

export interface PushResponse {
  cursor: number;
  accepted: PushAccepted[];
  conflicts: PushConflict[];
}

export interface ChangesResponse {
  changes: WireChange[];
  nextCursor: number;
  hasMore: boolean;
}

export interface SyncStatusResponse {
  cursorHead: number;
  notes: number;
  folders: number;
  attachments: number;
  vaultConfigured: boolean;
}

export type ResolveStrategy = 'keep_mine' | 'take_theirs' | 'fork_copy';

export interface ResolveResponse {
  note?: WireNote;
  folder?: WireFolder;
  copyId?: string;
}

export interface NoteVersionSummary {
  id: number;
  version: number;
  title: string;
  origin: string;
  actor: string;
  createdAt: number;
}

export interface NoteVersionDetail extends NoteVersionSummary {
  content: string;
}

export interface RepullReport {
  import: {
    skipped: 'already-imported' | 'library-not-empty' | 'no-bucket-data' | null;
    notes: number;
    folders: number;
    attachments: number;
    vaultMeta: boolean;
    vaultDek: boolean;
    failed: string[];
  } | null;
  importError: string | null;
}

export const syncApi = {
  status(): Promise<SyncStatusResponse> {
    return api.get<SyncStatusResponse>('/sync/status');
  },

  repull(): Promise<RepullReport> {
    return api.post<RepullReport>('/sync/repull', {});
  },

  push(changes: LocalChange[]): Promise<PushResponse> {
    return api.post<PushResponse>('/sync/push', { changes, device: deviceId() });
  },

  changes(since: number, limit = 500): Promise<ChangesResponse> {
    return api.get<ChangesResponse>(`/sync/changes?since=${since}&limit=${limit}`);
  },

  resolve(
    kind: 'note' | 'folder',
    id: string,
    strategy: ResolveStrategy,
    record?: unknown,
  ): Promise<ResolveResponse> {
    return api.post<ResolveResponse>('/sync/resolve', { kind, id, strategy, record, device: deviceId() });
  },

  noteVersions(noteId: string): Promise<{ versions: NoteVersionSummary[] }> {
    return api.get<{ versions: NoteVersionSummary[] }>(`/notes/${encodeURIComponent(noteId)}/versions`);
  },

  noteVersion(noteId: string, versionId: number): Promise<{ version: NoteVersionDetail }> {
    return api.get<{ version: NoteVersionDetail }>(
      `/notes/${encodeURIComponent(noteId)}/versions/${versionId}`,
    );
  },

  restoreVersion(noteId: string, versionId: number): Promise<{ note: WireNote }> {
    return api.post<{ note: WireNote }>(
      `/notes/${encodeURIComponent(noteId)}/versions/${versionId}/restore`,
      { device: deviceId() },
    );
  },
};
